<!-- 
A set of action buttons presented in a standard way.

On larger screens the supplied actions will be displayed as buttons on the LHS.
On smaller screens the actions will instead appear inside a single "Actions" menu dropdown.
Optionally a set of navigationActions can be supplied. These will be displayed on the RHS.
-->
<template>
  <div>
    <v-menu 
      v-if="hasActions || hasNavigationActions" 
      v-model="menu" 
      :class="menuBreakpoint"
      offset-y right 
    >
      <v-btn slot="activator" :disabled="busy" class="mr-0 ml-0 mb-3">
        <span class="mr-1">Actions</span>
        <our-expand-icon :expanded="menu"/>
      </v-btn>
      <div style="width: 300px">
        <h3 class="title pt-3 pl-3 pr-3 pb-2" style="background:white;">Actions</h3>
        <v-hover v-for="action in actions" :key="action.text" class="ma-0">
          <v-card 
            v-if="!action.disabled"
            v-ripple
            slot-scope="{ hover }"
            :color="hover ? 'grey lighten-4' : ''"
            flat tile
            style="cursor:pointer"
            class="pt-2 pl-3 pr-3 pb-2"
            @click="$emit(action.emit)"
            @keydown.enter="$emit(action.emit)"
          >
            <h4 class="subheading mb-2">
              <v-icon :color="hover ? 'primary' : ''" class="mr-1 task-icon">{{ action.icon }}</v-icon>
              {{ action.text }}
            </h4>
            <p v-if="action.description" class="body-1 mt-2 mb-1 grey--text text--darken-1">
              {{ action.description }}
            </p>
          </v-card>
          <v-card 
            v-else
            flat tile
            class="pt-2 pl-3 pr-3 pb-2"
            @click.stop
          >
            <h4 class="subheading mb-2 grey--text text--lighten-1">
              <v-icon class="mr-1 task-icon grey--text text--lighten-1">{{ action.icon }}</v-icon>
              {{ action.text }}
            </h4>
            <p v-if="action.description" class="body-1 mt-2 mb-1 grey--text text--darken-1">
              {{ action.description }}
            </p>
          </v-card>
        </v-hover>
        <v-hover v-for="action in navigationActions" :key="action.text" class="ma-0">
          <v-card 
            v-ripple
            slot-scope="{ hover }"
            :to="action.url"
            :color="hover ? 'grey lighten-4' : ''"
            flat tile
            style="cursor:pointer"
            class="pt-2 pl-3 pr-3 pb-2"
          >
            <h4 class="subheading mb-2">
              <v-icon :color="hover ? 'primary' : ''" class="mr-1 task-icon">{{ action.icon }}</v-icon>
              {{ action.text }}
            </h4>
            <p v-if="action.description" class="body-1 mt-2 mb-1 grey--text text--darken-1">
              {{ action.description }}
            </p>
          </v-card>
        </v-hover>
      </div>
    </v-menu>
    <div :class="buttonBreakpoint" class="our-actions-bar">
      <our-action-button 
        v-for="action in actions" 
        :key="action.text"
        :tooltip="action.description"
        :icon="action.icon"
        :saving="busy"
        :progress-indicator="action.progressIndicator ? true : false"
        :disabled="!!action.disabled"
        @click="$emit(action.emit)"
      >
        {{ action.text }}
      </our-action-button>
      <v-spacer/>
      <v-tooltip 
        v-for="action in navigationActions" 
        :key="action.text" 
        top open-delay="0" close-delay="0" transition="null"
      >
        <v-btn 
          slot="activator" 
          :disabled="busy"
          :to="action.url" 
          flat color="primary" class="ml-3 mr-0 mb-3"
        >
          <v-icon class="mr-2">{{ action.icon }}</v-icon>
          {{ action.text }}
        </v-btn>
        <span>{{ action.description }}</span>
      </v-tooltip>
      <slot/>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    actions: {
      type: Array,
      default: null
    },
    navigationActions: {
      type: Array,
      default: null
    },
    busy: {
      type: Boolean,
      default: false
    },
    xsBreakpoint: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      menu: false
    };
  },
  computed: {
    hasActions() {
      return this.actions != null && this.actions.length > 0;
    },
    hasNavigationActions() {
      return this.navigationActions != null && this.navigationActions.length > 0;
    },
    menuBreakpoint() {
      return this.xsBreakpoint ? "hidden-sm-and-up" : "hidden-md-and-up";
    },
    buttonBreakpoint() {
      return this.xsBreakpoint ? "hidden-xs-only" : "hidden-sm-and-down";
    }
  }
};
</script>

<style lang="scss">
.task-icon {
  position: relative;
  top: 2px;
}
.our-actions-bar {
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
}
</style>